import { ITeamInfo } from "../../types/types";
import { useLocalization } from "../../hooks/useLocalization";

import { TeamLogo } from "../shared/TeamLogo";

interface ScheduleTeamFilterProps {
  teams: ITeamInfo[];
  selectedTeamIds: Set<string>;
  onToggleTeam: (teamId: string) => void;
  onClear: () => void;
}

export const ScheduleTeamFilter = ({
  teams,
  selectedTeamIds,
  onToggleTeam,
  onClear,
}: ScheduleTeamFilterProps) => {
  const { strings } = useLocalization();

  if (!teams.length) {
    return null;
  }

  return (
    <section className="schedule-page-filter" aria-label={strings.SCHEDULE.FILTER_TEAMS}>
      <div className="schedule-page-filter-header">
        <h2 className="schedule-page-filter-title">{strings.SCHEDULE.FILTER_TEAMS}</h2>
        {selectedTeamIds.size > 0 && (
          <button type="button" className="schedule-page-filter-clear" onClick={onClear}>
            {strings.SCHEDULE.CLEAR_FILTER}
          </button>
        )}
      </div>

      <div className="schedule-page-filter-teams">
        {teams.map((team) => {
          const selected = selectedTeamIds.has(team.id);

          return (
            <button
              key={team.id}
              type="button"
              className={`inline-flex max-w-full cursor-pointer items-center gap-2 rounded-round border border-[var(--surface-card-border)] bg-[var(--surface-card-row-bg)] py-[0.45rem] pr-3 pl-[0.45rem] font-jetbrains text-white transition-[border-color] duration-200 ease-in-out hover:border-[var(--surface-card-hover-border)] max-md:min-h-11 max-md:px-3 max-md:py-2.5${selected ? " border-light-blue bg-[rgba(28,152,217,0.12)]" : ""}`}
              aria-pressed={selected}
              onClick={() => {
                onToggleTeam(team.id);
              }}
            >
              <TeamLogo
                teamId={team.id}
                alt={strings.COMMON.TEAM_LOGO_ALT.replace("{team}", team.name)}
                className="schedule-page-filter-team-logo"
                linkToTeam={false}
              />
              <span className="schedule-page-filter-team-name">{team.name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

interface ScheduleSeasonFilterProps {
  seasons: string[];
  selectedSeason: string;
  currentSeason: string;
  onSeasonChange: (season: string) => void;
  selectId?: string;
}

export const ScheduleSeasonFilter = ({
  seasons,
  selectedSeason,
  currentSeason,
  onSeasonChange,
  selectId = "schedule-season-select",
}: ScheduleSeasonFilterProps) => {
  const { strings } = useLocalization();

  const formatSeasonLabel = (season: string) =>
    season === currentSeason ? `${season} (${strings.RESULTS.CURRENT_SEASON_LIVE})` : season;

  if (!seasons.length) {
    return null;
  }

  return (
    <section
      className="schedule-page-filter schedule-page-filter-season"
      aria-label={strings.RESULTS.FILTER_SEASON}
    >
      <label className="schedule-page-filter-header" htmlFor={selectId}>
        <span className="schedule-page-filter-title">{strings.RESULTS.FILTER_SEASON}</span>
        <select
          id={selectId}
          className="schedule-page-filter-select"
          value={selectedSeason}
          onChange={(event) => {
            onSeasonChange(event.target.value);
          }}
        >
          {seasons.map((season) => (
            <option key={season} value={season}>
              {formatSeasonLabel(season)}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
};
